import { Directive, DestroyRef, ElementRef, NgZone, OnDestroy, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DropDownListComponent, MultiSelectComponent, ComboBoxComponent } from "@progress/kendo-angular-dropdowns";
import { Observable } from "rxjs";

interface DropDownLike {
    toggle(open: boolean): void;
    name?: string;
    scrolled?: boolean;
    opened: Observable<unknown>;
    closed: Observable<unknown>;
}

@Directive({
    selector: "[closeOnAncestorScroll]",
})
export class CloseOnAncestorScrollDirective implements OnInit, OnDestroy {
    private readonly destroyRef = inject(DestroyRef);
    private readonly hostEl = inject(ElementRef<HTMLElement>);
    private readonly zone = inject(NgZone);

    private readonly asDDL = inject(DropDownListComponent, { self: true, optional: true });
    private readonly asCombo = inject(ComboBoxComponent, { self: true, optional: true });
    private readonly asMulti = inject(MultiSelectComponent, { self: true, optional: true });

    private readonly ddl: DropDownLike | null = (this.asDDL ?? this.asCombo ?? this.asMulti) as unknown as DropDownLike | null;

    private intersectionObserver?: IntersectionObserver;

    ngOnInit(): void {
        this.ddl?.opened?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.attach());
        this.ddl?.closed?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.detach());
    }

    private attach() {
        this.setupIntersectionObserver();
    }

    private detach() {
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = undefined;
    }

    ngOnDestroy() {
        this.detach();
    }

    private setupIntersectionObserver() {
        this.zone.runOutsideAngular(() => {
            this.intersectionObserver = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => this.closeIfOutsideViewport(entry));
                },
                {
                    root: null,
                    threshold: 0,
                },
            );

            this.intersectionObserver.observe(this.hostEl.nativeElement);
        });
    }

    private closeIfOutsideViewport(entry: IntersectionObserverEntry) {
        if (!entry.intersectionRatio) {
            this.zone.run(() => {
                const isMulti = !!this.asMulti;
                if (isMulti) this.ddl!.scrolled = true;
                this.ddl!.toggle(false);
            });
        }
    }
}
